<template>
    <div v-if="loading">
        <div class="text-center py-4 rounded border border-dashed" :class="$store.state.darkMode ? 'dark-mode-bg' : 'bg-white'">
            <div class="spinner-border mb-2" role="status" style="width: 1.5em; height: 1.5em; opacity: 0.5;">
                <span class="visually-hidden">Loading...</span>
            </div>
            <p class="text-muted mb-0">Loading pipeline data...</p>
        </div>
    </div>
    <div v-else class="row left-nav-collapsible layout-wrapper">
        <div v-if="detailPageOpen" id="detail-page-open">
            <button class="btn details-open-btn" type="button" @click="onDetailPageOpenClose(false)">
                <span> <i class="fa fa-caret-right"></i></span>
            </button>
        </div>
        <TransitionGroup :name="jobs ? 'slide' : ''" key="detail">
            <div v-if="showCloseBtn && !detailPageOpen" id="detail-page-close" key="close">
                <button class="btn text-start details-open-btn" type="button" @click="onDetailPageOpenClose(true)">
                    <span> <i class="fa fa-caret-left"></i></span>
                </button>
            </div>

            <div class="side-col" v-if="!detailPageOpen" id="details-col" @mouseover="onDetailPageHover(true)"
                key="details-col">
                <div class="sticky-top rounded-2" style="top: 20px; z-index: 10;">
                    <nav class="nav flex-column side-nav">
                        <a :class="['nav-link first-nav-link', activeSections.includes('section-edge') ? 'active' : '']"
                            href="javascript:void(0)" @click.prevent="scrollToSection('section-edge')">Edges to
                            Extract</a>
                        <a :class="['nav-link', activeSections.includes('section-config') ? 'active' : '']"
                            href="javascript:void(0)"
                            @click.prevent="scrollToSection('section-config')">Configuration</a>
                        <a :class="['nav-link last-nav-link', activeSections.includes('section-oltp') ? 'active' : '']"
                            href="javascript:void(0)" @click.prevent="scrollToSection('section-oltp')">OLTP</a>
                    </nav>
                </div>
            </div>
            <div class="scroll-container col main-col card custom-card-body mx-3" id="main-col"
                @mouseover="onDetailPageHover(false)" key="main-col">
                <div id="section-edge">
                    <h4 class="my-4">
                        Edge Extract Configuration
                        <span class="fa fa-circle-info text-muted" title="Configure the tenant_id and extract fields."
                            data-bs-toggle="tooltip" data-bs-placement="right"></span>
                    </h4>
                    <div v-if="hasExtractConfig && extractConfig">
                        <EasyMode ref="extractConfigComponent"
                            :key="'extract-config-' + (pipelineJob ? pipelineJob.name : 'pipeline') + '-' + (loading ? 'loading' : 'loaded')"
                            v-model="extractConfig" :editMode="editMode" :invalidValues="invalidValues" :entityTypeOptions="entityTypeOptions" />
                    </div>
                    <div v-else>
                        <div class="alert alert-secondary mb-0 text-center">
                            <i class="fa fa-circle-info me-2"></i>
                            No Edge Extract configuration found!
                        </div>
                    </div>
                </div>
                <div id="section-config">
                    <hr class="my-4">
                    <h4 class="mb-4">
                        Configuration
                        <span class="fa fa-circle-info text-muted"
                            title="Configure values for all jobs created in this deployment. The changes will be updated in vars.yaml file for each job."
                            data-bs-toggle="tooltip" data-bs-placement="right"></span>
                    </h4>
                    <div v-if="clusterConfigFieldInfo && Object.values(clusterConfigFieldInfo).some(field => field.editable)">
                        <ClusterConfigEasyMode ref="clusterConfigComponent"
                            :key="'cluster-config-' + (pipelineJob ? pipelineJob.name : 'pipeline') + '-' + (loading ? 'loading' : 'loaded')"
                            v-model="clusterConfig" :fieldInfo="clusterConfigFieldInfo"
                            :cleanConfigFn="cleanClusterConfig" :editMode="editMode"
                            @invalidValuesChange="onInvalidValuesChange" />
                    </div>
                    <div v-else>
                        <div class="alert alert-secondary mb-0 text-center">
                            <i class="fa fa-circle-info me-2"></i>
                            No Cluster configuration found!
                        </div>
                    </div>
                </div>
                <div id="section-oltp">
                    <hr class="my-4">
                    <h4 class="my-4">
                        OLTP
                        <span class="fa fa-circle-info text-muted" title="Configure the OLTP fields."
                            data-bs-toggle="tooltip" data-bs-placement="right"></span>
                    </h4>
                    <div v-if="oltp">
                        <OltpConfigEasyMode ref="oltpConfigComponent"
                            :key="'oltp-config-' + (pipelineJob ? pipelineJob.name : 'pipeline') + '-' + (loading ? 'loading' : 'loaded')"
                            v-model="oltpConfig" :fieldInfo="oltpConfigFieldInfo" :cleanConfigFn="cleanOltpConfig"
                            :editMode="editMode" />
                    </div>
                    <div v-else>
                        <div class="alert alert-secondary mb-0 text-center">
                            <i class="fa fa-circle-info me-2"></i>
                            No OLTP configuration found!
                        </div>
                    </div>
                </div>
            </div>
        </TransitionGroup>
    </div>
</template>

<script>
import ClusterConfigEasyMode from "../../components/ClusterConfigEasyMode.vue";
import EasyMode from "../../components/EasyMode.vue";
import OltpConfigEasyMode from "../../components/OltpConfigEasyMode.vue";
import YAML from "yaml";
import axios from "axios";
import { mapActions } from 'vuex';
import { DateTimeFormat } from '../../utils/commonFunction';
export default {
    name: 'JobStackDeploymentDetailsEdit',
    components: {
        ClusterConfigEasyMode,
        EasyMode,
        OltpConfigEasyMode,
    },
    data() {
        return {
            detailPageOpen: false,
            showCloseBtn: false,
            activeSections: ['section-edge'], // Changed to an array
            sectionIds: ['section-config', 'section-oltp', 'section-edge'],

            uiMarkerStart: '# === UI:CONFIGURABLE_VALUES:START ===',
            uiMarkerEnd: '# === UI:CONFIGURABLE_VALUES:END ===',
            editError: "",
            extractConfig: null,
            hasExtractConfig: false,
            invalidValues: [],

            clusterConfig: null,
            clusterConfigFieldInfo: {
                graph_ttl: {
                    placeholder: "ttl (-1 for inf)",
                    editable: false,
                    tooltip: "This will hold the time to delete the node from its existing timestamp.",
                },
                create_cluster_weightage_map: {
                    editable: false,
                    tooltip: "This will provide the weightage value per type~domain. This will help with the algorithm of choosing the clusterID.",
                },
                create_cluster_invalid_ids: { // part of edge extract file, just sending some info about that field
                    initialValue: ['null'], // for reset to initial value
                    editable: false,
                    tooltip: "These nodes will be dropped and sent to the quarantined list",
                },
                create_cluster_ttl_maps: {
                    editable: false,
                    tooltip: "This will provide the weightage value per type~domain. This will help with the algorithm of choosing the clusterID.",
                },
                choose_cluster_type: {
                    placeholder: "c_type",
                    editable: false,
                    tooltip: "This configured type will be assigned as the cluster's type.",
                },
                choose_cluster_domain: {
                    placeholder: "c_domain",
                    editable: false,
                    tooltip: "This configured domain will be assigned as the cluster's domain.",
                }
            },

            remoteJobsInfo: {},
            currentJobsInfo: {},
            configDiff: [],
            conflictDetails: null,
            // Store anchor names per job per field: { jobName: { fieldName: anchorName } }
            jobAnchors: {},

            // oltp section
            oltp: false,
            oltpConfig: null,
            oltpConfigFieldInfo: {
                push_all_nodes_to_oltp: {
                    editable: false,
                    tooltip: "The value to send ALL nodes to OLTP in the neighbours list with the clusterID as the partition key.",
                },
                push_all_nodes_to_oltp_rev: {
                    editable: false,
                    tooltip: "The value to send ALL nodes to OLTP. This will be mapped from NodeID (as partition key) to ClusterID (as neighbour).",
                },
                reverse_oltp_nodes: {
                    editable: false,
                    tooltip: "<div>The provided nodes will be pushed to OLTP in <br>NodeID -> ClusterID format</div>",
                },
                cluster_oltp_nodes: {
                    editable: false,
                    tooltip: "<div>The provided nodes will be pushed to OLTP in <br>ClusterID -> NodeID format</div>",
                },
            },
        }
    },
    props: ['jobs', 'jobNames', 'deployment', 'loading', 'editMode', 'saveTrigger', 'saveAndRunTrigger', 'entityTypeOptions'],
    computed: {
        pipelineJob() {
            return Object.values(this.jobs).find(job => job.kind?.toLowerCase() === 'pipeline');
        },
        sandboxListOptions() {
            return this.$store.state.sandbox.prodV2Sandboxes ? this.$store.state.sandbox.prodV2Sandboxes.map((sandbox) => {
                return { "label": sandbox.name, "value": sandbox.name };
            }) : [];
        },
    },
    async mounted() {
        if (!this.loading) {
            const container = document.getElementById('main-col');
            if (container) container.addEventListener('scroll', this.handleManualScroll);
        }
    },
    beforeMount() {
        this.loadConfig();
        this.$emit('setEditButton', true);
        this.$emit('setPromoteButton', true);
        this.$emit('setRunAllButton', "edit");
    },
    beforeUnmount() {
        const container = document.getElementById('main-col');
        if (container) container.removeEventListener('scroll', this.handleManualScroll);
        this.$emit('setEditButton', false);
        this.$emit('setPromoteButton', false);
        this.$emit('setRunAllButton', "");
    },
    methods: {
        ...mapActions({
            showModal: 'showModal',
        }),
        DateTimeFormat,
        loadConfig() {
            // Initialize empty clusterConfig
            let extractedConfig = {
                graph_ttl: "",
                create_cluster_weightage_map: [], // array of objects with key and value
                create_cluster_ttl_maps: [], // array of objects with key and value
                choose_cluster_type: "",
                choose_cluster_domain: "",
                create_cluster_invalid_ids: [],
            };
            // Initialize extract config
            let edgeExtractConfigObj = null;
            this.hasExtractConfig = false;
            // Initialize jobAnchors
            this.jobAnchors = {};

            // Initialize oltp config
            this.oltpConfig = {
                // push_all_nodes_to_oltp_rev and reverse_oltp_nodes will be added below
                reverse_oltp_nodes: [],
                cluster_oltp_nodes: [],
            };

            // Iterate through all jobs and extract config from vars.yaml
            if (this.jobs && Object.keys(this.jobs).length > 0) {
                Object.values(this.jobs).forEach(job => {
                    if (job?.system_files) {
                        // Find vars.yaml file
                        const varsYamlFile = job.system_files.find(
                            file => file.relative_path === 'vars.yaml'
                        );

                        if (varsYamlFile?.content) {
                            try {
                                // Extract the configurable values section between markers
                                const configSection = this.extractConfigSection(varsYamlFile.content);

                                if (configSection) {
                                    // Extract anchor names from the YAML text (before parsing)
                                    const jobName = job?.name;
                                    const anchors = this.extractAllAnchorsFromYaml(configSection);
                                    if (Object.keys(anchors).length > 0) {
                                        this.jobAnchors[jobName] = anchors;
                                    }

                                    // Parse the extracted section as simple YAML
                                    const parsedYaml = YAML.parse(configSection);

                                    if (parsedYaml && typeof parsedYaml === 'object') {
                                        if (parsedYaml.choose_cluster_type && !extractedConfig.choose_cluster_type) {
                                            extractedConfig.choose_cluster_type = parsedYaml.choose_cluster_type;
                                            this.clusterConfigFieldInfo.choose_cluster_type.editable = true;
                                        }

                                        if (parsedYaml.choose_cluster_domain && !extractedConfig.choose_cluster_domain) {
                                            extractedConfig.choose_cluster_domain = parsedYaml.choose_cluster_domain;
                                            this.clusterConfigFieldInfo.choose_cluster_domain.editable = true;
                                        }

                                        if (parsedYaml.graph_ttl && !extractedConfig.graph_ttl) {
                                            extractedConfig.graph_ttl = parsedYaml.graph_ttl;
                                            this.clusterConfigFieldInfo.graph_ttl.editable = true;
                                        }

                                        if (parsedYaml.create_cluster_weightage_map && extractedConfig.create_cluster_weightage_map.length === 0) {
                                            extractedConfig.create_cluster_weightage_map = Object.entries(parsedYaml.create_cluster_weightage_map).map(([key, value]) => {
                                                return {
                                                    key: key,
                                                    value: value
                                                };
                                            });
                                            this.clusterConfigFieldInfo.create_cluster_weightage_map.editable = true;
                                        }

                                        if (parsedYaml.create_cluster_ttl_maps && extractedConfig.create_cluster_ttl_maps.length === 0) {
                                            extractedConfig.create_cluster_ttl_maps = Object.entries(parsedYaml.create_cluster_ttl_maps).map(([key, value]) => {
                                                return {
                                                    key: key,
                                                    value: value
                                                };
                                            });
                                            this.clusterConfigFieldInfo.create_cluster_ttl_maps.editable = true;
                                        }

                                        if (parsedYaml.create_cluster_invalid_ids && extractedConfig.create_cluster_invalid_ids.length === 0) {
                                            extractedConfig.create_cluster_invalid_ids = parsedYaml.create_cluster_invalid_ids;
                                            this.clusterConfigFieldInfo.create_cluster_invalid_ids.editable = true;
                                        }

                                        // Check for tenant_id and extract fields
                                        if ((parsedYaml.tenant_id !== undefined || parsedYaml.extract !== undefined) && !edgeExtractConfigObj) {
                                            edgeExtractConfigObj = {};
                                            if (parsedYaml.tenant_id !== undefined) {
                                                edgeExtractConfigObj.tenant_id = parsedYaml.tenant_id;
                                            }
                                            if (parsedYaml.extract !== undefined) {
                                                edgeExtractConfigObj.extract = parsedYaml.extract;
                                            }
                                            this.hasExtractConfig = true;
                                        }

                                        // Check for oltp config
                                        if (Object.hasOwn(parsedYaml, 'push_all_nodes_to_oltp') && !Object.hasOwn(this.oltpConfig, 'push_all_nodes_to_oltp')) {
                                            this.oltpConfig.push_all_nodes_to_oltp = parsedYaml.push_all_nodes_to_oltp;
                                            this.oltpConfigFieldInfo.push_all_nodes_to_oltp.editable = true;
                                        }

                                        if (Object.hasOwn(parsedYaml, 'push_all_nodes_to_oltp_rev') && !Object.hasOwn(this.oltpConfig, 'push_all_nodes_to_oltp_rev')) {
                                            this.oltpConfig.push_all_nodes_to_oltp_rev = parsedYaml.push_all_nodes_to_oltp_rev;
                                            this.oltpConfigFieldInfo.push_all_nodes_to_oltp_rev.editable = true;
                                        }

                                        if (parsedYaml.reverse_oltp_nodes && this.oltpConfig.reverse_oltp_nodes.length === 0) {
                                            this.oltpConfig.reverse_oltp_nodes = parsedYaml.reverse_oltp_nodes;
                                            this.oltpConfigFieldInfo.reverse_oltp_nodes.editable = true;
                                        }

                                        if (parsedYaml.cluster_oltp_nodes && this.oltpConfig.cluster_oltp_nodes.length === 0) {
                                            this.oltpConfig.cluster_oltp_nodes = parsedYaml.cluster_oltp_nodes;
                                            this.oltpConfigFieldInfo.cluster_oltp_nodes.editable = true;
                                        }
                                    }
                                }

                                // check pipeline job's vars file to skip oltp or not
                                if (job?.kind?.toLowerCase() === 'pipeline') {
                                    
                                    const documents = YAML.parseAllDocuments(varsYamlFile.content);

                                    // Check if ANY document has the OLTP flags
                                    const hasOltpConfig = documents.some(doc => {
                                        if (!doc) return false; // skip null/empty docs
                                        const parsedDoc = doc.toJS();
                                        
                                        const isOltpTrue = Object.hasOwn(parsedDoc, 'is_oltp') && parsedDoc.is_oltp === true;
                                        const notOltpFalse = Object.hasOwn(parsedDoc, 'not_oltp') && parsedDoc.not_oltp === false;
                                        
                                        return isOltpTrue || notOltpFalse;
                                    });

                                    if (hasOltpConfig) {
                                        console.log(`OLTP configuration detected in job ${job.name}, showing OLTP section`);
                                        this.oltp = true;
                                    }
                                }
                            } catch (e) {
                                console.warn(`Failed to parse vars.yaml for job ${job.name}:`, e);
                            }
                        }
                    }
                });
            }
            // Create a new object reference to ensure reactivity
            this.clusterConfig = { ...extractedConfig };

            // set initial values for the cluster config fields
            this.clusterConfigFieldInfo.choose_cluster_type.initialValue = JSON.parse(JSON.stringify(this.clusterConfig.choose_cluster_type));
            this.clusterConfigFieldInfo.choose_cluster_domain.initialValue = JSON.parse(JSON.stringify(this.clusterConfig.choose_cluster_domain));
            this.clusterConfigFieldInfo.graph_ttl.initialValue = JSON.parse(JSON.stringify(this.clusterConfig.graph_ttl));
            this.clusterConfigFieldInfo.create_cluster_weightage_map.initialValue = JSON.parse(JSON.stringify(this.clusterConfig.create_cluster_weightage_map));
            this.clusterConfigFieldInfo.create_cluster_ttl_maps.initialValue = JSON.parse(JSON.stringify(this.clusterConfig.create_cluster_ttl_maps));
            this.clusterConfigFieldInfo.create_cluster_invalid_ids.initialValue = JSON.parse(JSON.stringify(this.clusterConfig.create_cluster_invalid_ids));

            // set initial values for the oltp config fields
            if (Object.hasOwn(this.oltpConfig, 'push_all_nodes_to_oltp')) {
                this.oltpConfigFieldInfo.push_all_nodes_to_oltp.initialValue = JSON.parse(JSON.stringify(this.oltpConfig.push_all_nodes_to_oltp));
            }
            if (Object.hasOwn(this.oltpConfig, 'push_all_nodes_to_oltp_rev')) {
                this.oltpConfigFieldInfo.push_all_nodes_to_oltp_rev.initialValue = JSON.parse(JSON.stringify(this.oltpConfig.push_all_nodes_to_oltp_rev));
            }
            this.oltpConfigFieldInfo.reverse_oltp_nodes.initialValue = JSON.parse(JSON.stringify(this.oltpConfig.reverse_oltp_nodes));
            this.oltpConfigFieldInfo.cluster_oltp_nodes.initialValue = JSON.parse(JSON.stringify(this.oltpConfig.cluster_oltp_nodes));

            // Set extract config as YAML string if it exists
            if (edgeExtractConfigObj) {
                this.extractConfig = YAML.stringify(edgeExtractConfigObj);
            } else {
                this.extractConfig = null;
            }

            // set initial values for the invalid values field
            this.invalidValues = JSON.parse(JSON.stringify(this.clusterConfig.create_cluster_invalid_ids));

            // this.clusterConfigFieldInfo.invalid_values.editable = this.hasExtractConfig && this.extractConfig ? true : false;
        },
        extractConfigSection(yamlContent) {
            if (!yamlContent || typeof yamlContent !== 'string') {
                return null;
            }

            const startMarker = this.uiMarkerStart;
            const endMarker = this.uiMarkerEnd;

            const startIndex = yamlContent.indexOf(startMarker);
            const endIndex = yamlContent.indexOf(endMarker);

            if (startIndex === -1 || endIndex === -1 || startIndex >= endIndex) {
                return null;
            }

            // Extract the section between markers (excluding the markers themselves)
            const sectionStart = startIndex + startMarker.length;
            const sectionEnd = endIndex;
            const section = yamlContent.substring(sectionStart, sectionEnd).trim();

            return section || null;
        },
        /**
         * Check if a job has UI markers with fields defined in clusterConfigFieldInfo
         * @param {Object} job - Job object
         * @returns {boolean} - True if job has UI markers with any clusterConfigFieldInfo fields
         */
        hasUIMarkersWithValues(job) {
            if (!job?.system_files) {
                return false;
            }

            // Find vars.yaml file
            const varsYamlFile = job.system_files.find(
                file => file.relative_path === 'vars.yaml'
            );

            if (!varsYamlFile?.content) {
                return false;
            }

            // Extract the configurable values section between markers
            const configSection = this.extractConfigSection(varsYamlFile.content);

            if (!configSection) {
                return false;
            }

            try {
                // Parse the extracted section as YAML
                const parsedYaml = YAML.parse(configSection);

                if (!parsedYaml || typeof parsedYaml !== 'object') {
                    return false;
                }

                // Get all field names from clusterConfigFieldInfo
                const fieldInfoKeys = Object.keys(this.clusterConfigFieldInfo);

                // Check if any of the parsed YAML keys match fields in clusterConfigFieldInfo
                const hasFieldInfoFields = Object.keys(parsedYaml).some(key => {
                    return fieldInfoKeys.includes(key);
                });

                return hasFieldInfoFields;
            } catch (e) {
                console.warn(`Failed to parse config section for job ${job.name}:`, e);
                return false;
            }
        },
        /**
         * Extract all anchor names from YAML text for all configurable fields using parseDocument
         * @param {string} yamlText - YAML text content
         * @returns {Object} - Object with field names as keys and anchor names as values
         */
        extractAllAnchorsFromYaml(yamlText) {
            const anchors = {};
            if (!yamlText || typeof yamlText !== 'string') {
                return anchors;
            }

            try {
                // Use parseDocument to parse YAML and preserve anchor information
                const doc = YAML.parseDocument(yamlText);
                if (!doc || !doc.contents) {
                    return anchors;
                }

                // Get the root mapping node
                const rootNode = doc.contents;

                // Check if it's a map (kind can be 'MAP' or we check for items property)
                if (!rootNode.items || !Array.isArray(rootNode.items)) {
                    return anchors;
                }

                // Iterate through all items in the YAML map
                rootNode.items.forEach(item => {
                    if (item && item.key && item.value) {
                        const fieldName = item.key.value;
                        const valueNode = item.value;

                        // Check if the value node has an anchor
                        if (valueNode.anchor) {
                            anchors[fieldName] = valueNode.anchor;
                        }
                    }
                });
            } catch (e) {
                console.warn('Failed to parse YAML document for anchor extraction:', e);
            }
            return anchors;
        },
        onInvalidValuesChange(invalidValues) {
            this.invalidValues = invalidValues;
        },
        cleanClusterConfig(config) {
            // Helper method to clean cluster config
            if (!config || typeof config !== 'object') {
                return {};
            }

            const cleaned = {};

            if (Object.hasOwn(config, 'graph_ttl')) {
                let ttl = String(config.graph_ttl).trim();
                if (ttl == "-1") {
                    cleaned.graph_ttl = "inf";
                } else {
                    if (ttl == "") ttl = "0";
                    cleaned.graph_ttl = ttl + "h"; // add 'h' to the end
                }
            }

            if (config.choose_cluster_type) cleaned.choose_cluster_type = config.choose_cluster_type;
            if (config.choose_cluster_domain) cleaned.choose_cluster_domain = config.choose_cluster_domain;
            if (config.create_cluster_invalid_ids) cleaned.create_cluster_invalid_ids = config.create_cluster_invalid_ids;

            if (config.create_cluster_weightage_map && config.create_cluster_weightage_map.length > 0) {
                const weightageMap = [];
                config.create_cluster_weightage_map.forEach(item => {
                    if (item.key !== undefined && item.key !== null && item.value !== undefined && item.value !== null) {
                        let mapItem = JSON.parse(JSON.stringify(item));
                        weightageMap.push(mapItem);
                    }
                });
                cleaned.create_cluster_weightage_map = weightageMap;
            }

            if (config.create_cluster_ttl_maps && config.create_cluster_ttl_maps.length > 0) {
                const ttlMaps = [];
                config.create_cluster_ttl_maps.forEach(item => {
                    if (item.key !== undefined && item.key !== null && item.value !== undefined && item.value !== null) {
                        let mapItem = JSON.parse(JSON.stringify(item));
                        let ttl = String(mapItem.value).trim();
                        if (ttl == "-1") {
                            mapItem.value = "inf";
                        } else {
                            if (ttl == "") ttl = "0";
                            mapItem.value = ttl + "h"; // add 'h' to the end
                        }
                        ttlMaps.push(mapItem);
                    }
                });
                cleaned.create_cluster_ttl_maps = ttlMaps;
            }

            return cleaned;
        },
        cleanOltpConfig(config) {
            // Helper method to clean oltp config
            if (!config || typeof config !== 'object') {
                return {};
            }

            const cleaned = {};
            if (Object.hasOwn(config, 'push_all_nodes_to_oltp') && typeof config.push_all_nodes_to_oltp === 'boolean') cleaned.push_all_nodes_to_oltp = config.push_all_nodes_to_oltp;
            if (Object.hasOwn(config, 'push_all_nodes_to_oltp_rev') && typeof config.push_all_nodes_to_oltp_rev === 'boolean') cleaned.push_all_nodes_to_oltp_rev = config.push_all_nodes_to_oltp_rev;

            // Only include array if it has items
            if (Array.isArray(config.reverse_oltp_nodes) && config.reverse_oltp_nodes.length > 0) {
                cleaned.reverse_oltp_nodes = config.reverse_oltp_nodes;
            }
            if (Array.isArray(config.cluster_oltp_nodes) && config.cluster_oltp_nodes.length > 0) {
                cleaned.cluster_oltp_nodes = config.cluster_oltp_nodes;
            }

            return cleaned;
        },
        convertTTLandWeightageToMap(config) {
            if (config.create_cluster_weightage_map?.length > 0) {
                let weightageMap = {};
                config.create_cluster_weightage_map.forEach(item => {
                    weightageMap[item.key] = parseFloat(String(item.value));
                });
                config.create_cluster_weightage_map = weightageMap;
            }
            if (config.create_cluster_ttl_maps?.length > 0) {
                let ttlMaps = {};
                config.create_cluster_ttl_maps.forEach(item => {
                    ttlMaps[item.key] = item.value;
                });
                config.create_cluster_ttl_maps = ttlMaps;
            }
            return config;
        },
        async saveConfiguration(config, oltpConfig, extractConfig) {
            this.$emit('saving', true);
            this.editError = "";

            try {
                config = this.convertTTLandWeightageToMap(config);
                const result = await this.updateJobsConfig(config, oltpConfig, extractConfig);
                // If there are conflicts, the config changes tab will be shown automatically
                if (result.hasConflicts) {
                    this.$store.dispatch("addToast", {
                        message: "Configuration conflicts detected. Please review the changes.",
                        type: "warning",
                    });
                    return;
                }

                if (result.total === 0) {
                    this.$store.dispatch("addToast", {
                        message: `No configurable values found inside UI markers.`,
                        type: "info",
                    });
                } else if (result.successful === result.total) {
                    this.$store.dispatch("addToast", {
                        message: `Configuration saved successfully.`,
                        type: "success",
                    });
                    this.$emit('setEditMode', false);
                    await this.refreshJobsData(); // reload jobs data
                } else {
                    console.error(`Failed to update ${result.total - result.successful} out of ${result.total} job(s)`);
                    this.$store.dispatch("addToast", {
                        message: `Something went wrong while saving configuration. Please contact support.`,
                        type: "error",
                    });
                }
            } catch (error) {
                console.error("Error saving configuration: ", error);
                this.$store.dispatch("addToast", {
                    message: `Something went wrong while saving configuration. Please contact support.`,
                    type: "error",
                });
            } finally {
                this.$emit('saving', false);
            }
        },
        async saveConfigurationAndRun(config, oltpConfig, extractConfig) {
            this.$emit('saving', true);
            this.editError = "";

            try {
                config = this.convertTTLandWeightageToMap(config);
                const result = await this.updateJobsConfig(config, oltpConfig, extractConfig);

                // If there are conflicts, the config changes tab will be shown automatically
                if (result.hasConflicts) {
                    this.$store.dispatch("addToast", {
                        message: "Configuration conflicts detected. Please review the changes.",
                        type: "warning",
                    });
                    return;
                }

                if (result.total === 0) {
                    this.$store.dispatch("addToast", {
                        message: `No configurable values found inside UI markers.`,
                        type: "info",
                    });
                } else if (result.successful === result.total) {
                    this.$store.dispatch("addToast", {
                        message: `Configuration saved successfully.`,
                        type: "success",
                    });
                    this.$emit('setEditMode', false);
                    this.executePipelineJob();
                } else {
                    console.error(`Failed to update ${result.total - result.successful} out of ${result.total} job(s)`);
                    this.$store.dispatch("addToast", {
                        message: `Something went wrong while saving configuration. Please contact support.`,
                        type: "error",
                    });
                }
            } catch (error) {
                console.error("Error saving configuration: ", error);
                this.$store.dispatch("addToast", {
                    message: `Something went wrong while saving configuration. Please contact support.`,
                    type: "error",
                });
            } finally {
                this.$emit('saving', false);
            }
        },
        /**
         * Update the config section in vars.yaml while preserving content outside markers and extra fields inside
         * @param {string} yamlContent - Original vars.yaml content
         * @param {Object} newConfig - New config values from UI
         * @param {string} jobName - Job name to get anchor names for
         * @param {string} extractConfigYaml - Extract config as YAML string (optional)
         * @returns {string} - Updated vars.yaml content
         */
        updateConfigSection(jobName, yamlContent, newConfig, oltpConfig, extractConfigYaml = null) {
            const startMarker = this.uiMarkerStart;
            const endMarker = this.uiMarkerEnd;

            const startIndex = yamlContent.indexOf(startMarker);
            const endIndex = yamlContent.indexOf(endMarker);

            // If markers don't exist, return original content
            if (startIndex === -1 || endIndex === -1 || startIndex >= endIndex) {
                return yamlContent;
            }

            // Extract content before and after markers
            const beforeSection = yamlContent.substring(0, startIndex);
            const afterSection = yamlContent.substring(endIndex + endMarker.length);

            // Extract and parse existing config section
            const sectionStart = startIndex + startMarker.length;
            const sectionEnd = endIndex;
            const existingSection = yamlContent.substring(sectionStart, sectionEnd).trim();

            // Get stored anchor names for this job, or extract from existing section if not stored
            let jobAnchors = this.jobAnchors[jobName] || {};
            if (!jobAnchors || Object.keys(jobAnchors).length === 0) {
                // Fallback: extract anchors from existing section if not already stored
                jobAnchors = this.extractAllAnchorsFromYaml(existingSection);
            }

            let existingConfig = {};
            if (existingSection) {
                try {
                    existingConfig = YAML.parse(existingSection) || {};
                } catch (e) {
                    console.warn('Failed to parse existing config section:', e);
                    existingConfig = {};
                }
            }

            // Only update fields that already exist in the config section
            // Filter newConfig to only include keys that exist in existingConfig
            // This ensures we don't add new fields, only update existing ones
            const filteredNewConfig = {};
            Object.keys(newConfig).forEach(key => {
                if (Object.prototype.hasOwnProperty.call(existingConfig, key)) {
                    // Only update if the field exists in the original config
                    // Update even if the new value is empty/null (user might want to clear it)
                    filteredNewConfig[key] = newConfig[key];
                }
            });

            // Merge oltp config with existing config
            Object.keys(oltpConfig).forEach(key => {
                if (Object.prototype.hasOwnProperty.call(existingConfig, key)) {
                    // Only update if the field exists in the original config
                    // Update even if the new value is empty/null (user might want to clear it)
                    filteredNewConfig[key] = oltpConfig[key];
                }
            });

            // Merge filtered new config with existing config (only update existing fields, preserve all others)
            let mergedConfig = {
                ...existingConfig,
                ...filteredNewConfig
            };

            // Check if tenant_id or extract fields exist in the original config
            let edgeExtractConfigAvailable = false;
            if (extractConfigYaml) {
                if (existingConfig.tenant_id !== undefined || existingConfig.extract !== undefined) {
                    edgeExtractConfigAvailable = true;

                    // Delete tenant_id and extract from config if extract config is provided and they exist in original config
                    delete mergedConfig.tenant_id;
                    delete mergedConfig.extract;
                }
            }

            // Convert merged config back to YAML string with anchor format
            // Uses stored anchor names if available, otherwise falls back to field name
            const formatYamlWithAnchor = (key, value) => {
                // Get anchor name for this field, fallback to key if not found
                const anchorName = jobAnchors[key] || key;

                // Handle simple values (string, number, boolean, null)
                if (value === null || value === undefined) {
                    return `${key}: &${anchorName} `;
                } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                    return `${key}: &${anchorName} ${value}`;
                } else if (Array.isArray(value)) {
                    // For arrays, format as JSON string inline
                    const jsonStr = JSON.stringify(value);
                    return `${key}: &${anchorName} ${jsonStr}`;
                } else if (typeof value === 'object') {
                    // For objects, format as JSON string inline
                    const jsonStr = JSON.stringify(value);
                    return `${key}: &${anchorName} ${jsonStr}`;
                }
                return `${key}: &${anchorName} ${String(value)}`;
            };

            let updatedSection = Object.keys(mergedConfig)
                .map(key => formatYamlWithAnchor(key, mergedConfig[key]))
                .join('\n');

            // append the easy mode edge extract yaml with anchors
            if (extractConfigYaml && edgeExtractConfigAvailable) {
                try {
                    const lines = extractConfigYaml.split('\n');
                    const processedLines = [];

                    for (let i = 0; i < lines.length; i++) {
                        const line = lines[i];
                        const trimmedLine = line.trim();

                        // Check if this line is a field we need to add anchor to
                        if (trimmedLine.startsWith('tenant_id:') || trimmedLine.startsWith('extract:')) {
                            const fieldName = trimmedLine.split(':')[0].trim();
                            const anchorName = jobAnchors[fieldName] || fieldName;

                            // Check if anchor is already inline
                            if (trimmedLine.includes('&')) {
                                processedLines.push(line);
                            } else {
                                // Check if next line has an anchor
                                if (i + 1 < lines.length && lines[i + 1].trim().startsWith('&')) {
                                    // Combine current line with anchor from next line
                                    const indent = line.match(/^(\s*)/)[1];
                                    processedLines.push(`${indent}${fieldName}: &${anchorName}`);
                                    i++; // Skip the next line since we've merged it
                                } else {
                                    // Add anchor inline to current line
                                    processedLines.push(line.replace(new RegExp(`^\\s*${fieldName}:`), `${line.match(/^(\s*)/)[1]}${fieldName}: &${anchorName}`));
                                }
                            }
                        } else {
                            processedLines.push(line);
                        }
                    }

                    const extractYamlWithAnchors = processedLines.join('\n');
                    updatedSection = updatedSection + '\n' + extractYamlWithAnchors;
                } catch (e) {
                    console.warn('Failed to add anchors to extract config YAML:', e);
                    // Fallback: append as-is if parsing fails
                    updatedSection = updatedSection + '\n' + extractConfigYaml;
                }
            }

            // Reconstruct the full vars.yaml content
            const updatedContent = beforeSection +
                startMarker + '\n' +
                '# Managed by UI - manual edits may be overwritten\n' +
                updatedSection + '\n' +
                endMarker +
                afterSection;
            return updatedContent;
        },
        async updateJobsConfig(newConfig, oltpConfig, extractConfigYaml = null) {
            if (!this.jobs || Object.keys(this.jobs).length === 0) {
                return {
                    total: 0,
                    successful: 0
                };
            }

            // Filter jobs that have vars.yaml with config markers
            const jobsToUpdate = Object.values(this.jobs).filter(job => {
                if (!job?.system_files) {
                    return false;
                }
                const varsYamlFile = job.system_files.find(
                    file => file.relative_path === 'vars.yaml'
                );
                if (!varsYamlFile?.content) {
                    return false;
                }
                // Check if markers exist
                const hasMarkers = varsYamlFile.content.includes(this.uiMarkerStart) &&
                    varsYamlFile.content.includes(this.uiMarkerEnd);
                return hasMarkers;
            });

            if (jobsToUpdate.length === 0) {
                return {
                    total: 0,
                    successful: 0
                };
            }

            const cid = this.$store.state.currentClient?.cid;
            const sandboxName = this.deployment?.sandbox || this.modalData?.sandboxName;
            const lakeviewUrl = this.$store.state.lakeviewUrl;
            const accessToken = this.$store.state.accessToken;

            // Reset config diff and conflict details
            this.configDiff = [];
            this.conflictDetails = null;

            // Construct payload and update the current jobs info
            const currentJobsMap = {};
            jobsToUpdate.forEach((job) => {
                const varsYamlFile = job.system_files.find(
                    file => file.relative_path === 'vars.yaml'
                );

                const jobName = job?.name;

                // Update the vars.yaml content
                const updatedContent = this.updateConfigSection(
                    jobName,
                    varsYamlFile.content,
                    newConfig,
                    oltpConfig,
                    extractConfigYaml
                );

                // Update the job object
                const updatedJob = {
                    ...job,
                    system_files: job.system_files.map(file => {
                        if (file.relative_path === 'vars.yaml') {
                            return {
                                ...file,
                                content: updatedContent
                            };
                        }
                        return file;
                    })
                };
                currentJobsMap[updatedJob.name] = updatedJob;
            });
            this.currentJobsInfo = currentJobsMap;

            // Get all jobs from remote to check for conflicts
            const getJobsPromises = jobsToUpdate.map((job) => {
                const jobName = job?.name;
                return axios.get(
                    `${lakeviewUrl}/v1/cids/${cid}/sandboxes/${sandboxName}/jobs/${jobName}`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                ).then(response => {
                    return {
                        jobName: jobName,
                        data: response.data
                    };
                }).catch(error => {
                    return {
                        jobName: jobName,
                        error: error
                    };
                });
            });

            // Wait for all GET jobs to complete using Promise.allSettled
            const results = await Promise.allSettled(getJobsPromises);
            const remoteJobsMap = {};
            let hasConflicts = false;
            const diff = [];

            results.forEach((result, index) => {
                const job = jobsToUpdate[index];
                const jobName = job?.name;

                if (result.status === 'fulfilled' && result.value.data) {
                    const remoteJob = result.value.data;
                    remoteJobsMap[jobName] = remoteJob;

                    const currentJob = currentJobsMap[jobName];

                    // Check if job was updated since we last fetched it
                    if (remoteJob.updated_at !== job.updated_at) {
                        hasConflicts = true;

                        // Compare all files (system_files and user_files) between current and remote
                        ["system_files", "user_files"].forEach((fileType) => {
                            const currentFiles = currentJob[fileType] || [];
                            const remoteFiles = remoteJob[fileType] || [];

                            // Check files that exist in remote (may have been updated or are new)
                            remoteFiles.forEach((remoteFile) => {
                                const currentFile = currentFiles.find(
                                    f => f.relative_path === remoteFile.relative_path
                                );

                                // If file doesn't exist in current, it's a new file in remote
                                if (!currentFile) {
                                    diff.push({
                                        jobName: jobName,
                                        prev: {
                                            relative_path: remoteFile.relative_path,
                                            content: ''
                                        },
                                        current: {
                                            relative_path: remoteFile.relative_path,
                                            content: remoteFile.content || ''
                                        }
                                    });
                                } else if (currentFile.content !== remoteFile.content) {
                                    // File exists in both but content differs
                                    diff.push({
                                        jobName: jobName,
                                        prev: {
                                            relative_path: currentFile.relative_path,
                                            content: currentFile.content || ''
                                        },
                                        current: {
                                            relative_path: remoteFile.relative_path,
                                            content: remoteFile.content || ''
                                        }
                                    });
                                }
                            });

                            // Check files that exist in current but not in remote (may have been deleted)
                            currentFiles.forEach((currentFile) => {
                                const remoteFile = remoteFiles.find(
                                    f => f.relative_path === currentFile.relative_path
                                );

                                if (!remoteFile) {
                                    // File exists in current but not in remote (deleted in remote)
                                    diff.push({
                                        jobName: jobName,
                                        prev: {
                                            relative_path: currentFile.relative_path,
                                            content: currentFile.content || ''
                                        },
                                        current: {
                                            relative_path: currentFile.relative_path,
                                            content: ''
                                        }
                                    });
                                }
                            });
                        });
                    }
                }
            });


            this.remoteJobsInfo = remoteJobsMap;
            this.configDiff = diff;

            // If there are conflicts, show the config changes tab
            if (hasConflicts && diff.length > 0) {
                // Get the most recent update details
                const latestRemoteJob = Object.values(remoteJobsMap)
                    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];
                if (latestRemoteJob) {
                    this.conflictDetails = {
                        updated_by: latestRemoteJob.updated_by || 'Unknown',
                        updated_at: latestRemoteJob.updated_at
                    };
                }

                this.showModal({
                    component: "ConfigChanges",
                    data: {
                        diff: this.configDiff,
                        details: this.conflictDetails,
                        onReload: this.onReloadConfig,
                        onOverWrite: this.onOverWriteConfig,
                        screensize: 'xl'
                    }
                })

                return {
                    total: jobsToUpdate.length,
                    successful: 0,
                    hasConflicts: true
                };
            }

            // No conflicts or force update - proceed with actual updates
            const updatePromises = Object.values(currentJobsMap).map((updatedJob) => {
                return axios.put(
                    `${lakeviewUrl}/v1/cids/${cid}/sandboxes/${sandboxName}/jobs/${updatedJob.name}`,
                    updatedJob,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );
            });

            // Wait for all updates to complete using Promise.allSettled
            const updateResults = await Promise.allSettled(updatePromises);
            let successfulUpdates = 0;

            updateResults.forEach((result) => {
                if (result.status === 'fulfilled') {
                    successfulUpdates++;
                } else {
                    console.error('Failed to update job: ', result.reason.response?.data);
                }
            });

            return {
                total: jobsToUpdate.length,
                successful: successfulUpdates,
                hasConflicts: false
            };
        },
        async executePipelineJob() {
            if (!this.pipelineJob) {
                console.log('No pipeline job found..');
                return;
            }
            try {
                const cid = this.$store.state.currentClient?.cid;
                const sandboxName = this.deployment?.sandbox || this.modalData?.sandboxName;
                const response = await axios.post(
                    `${this.$store.state.lakeviewUrl}/v1/cids/${cid}/sandboxes/${sandboxName}/jobs/${this.pipelineJob.name}/executions`, {},
                    {
                        headers: {
                            Authorization: `Bearer ${this.$store.state.accessToken}`,
                        },
                    }
                );
                this.$emit('triggerPipelineStatusCheck', response.data?.execution_id);
                this.$router.push({
                    name: "Job Stack Deployment Details Pipeline",
                    params: {
                        sandboxId: sandboxName,
                        id: `${this.$route.params.id}`
                    }
                });
            } catch (error) {
                this.$store.dispatch("addToast", {
                    message: error.response?.data?.error || error.message || "Failed to execute pipeline. Please contact support.",
                    type: "error",
                });
            }
        },
        async onReloadConfig() {
            this.$store.dispatch('hideModal');

            // Clear the diff data
            this.configDiff = [];
            this.conflictDetails = null;
            await this.refreshJobsData(); //reload jobs data
        },
        async onOverWriteConfig() {
            const cid = this.$store.state.currentClient?.cid;
            const sandboxName = this.deployment?.sandbox || this.modalData?.sandboxName;
            const lakeviewUrl = this.$store.state.lakeviewUrl;
            const accessToken = this.$store.state.accessToken;

            try {
                // Create update promises for all jobs in currentJobsInfo
                const updatePromises = Object.values(this.currentJobsInfo).map((updatedJob) => {
                    return axios.put(
                        `${lakeviewUrl}/v1/cids/${cid}/sandboxes/${sandboxName}/jobs/${updatedJob.name}`,
                        updatedJob,
                        {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                            },
                        }
                    );
                });

                // Wait for all updates to complete using Promise.allSettled
                const updateResults = await Promise.allSettled(updatePromises);
                let successfulUpdates = 0;
                const total = Object.keys(this.currentJobsInfo).length;

                updateResults.forEach((result) => {
                    if (result.status === 'fulfilled') {
                        successfulUpdates++;
                    } else {
                        console.error('Failed to update job: ', result.reason.response?.data);
                    }
                });

                // Show toasts based on results
                if (successfulUpdates === total) {
                    this.$store.dispatch("addToast", {
                        message: `Configuration overwritten successfully.`,
                        type: "success",
                    });

                    this.$store.dispatch('hideModal');

                    // Clear the diff data
                    this.configDiff = [];
                    this.conflictDetails = null;
                    await this.refreshJobsData(); //reload jobs data
                } else {
                    console.error(`Failed to overwrite ${total - successfulUpdates} out of ${total} job(s)`);
                    this.$store.dispatch("addToast", {
                        message: `Something went wrong while overwriting configuration. Please contact support.`,
                        type: "error",
                    });
                }
            } catch (error) {
                console.error("Error overwriting configuration: ", error);
                this.$store.dispatch("addToast", {
                    message: "Something went wrong when overwriting configuration. Please contact support.",
                    type: "error",
                });
            }
        },
        async refreshJobsData() {
            // emit a refreshJobsDetails event
            this.$emit('refreshJobsDetails');
        },

        // transition effect methods
        onDetailPageOpenClose(value) {
            this.showCloseBtn = false;
            this.detailPageOpen = value;
        },
        onDetailPageHover(value) {
            this.showCloseBtn = value;
        },

        // navigation effect
        scrollToSection(id) {
            const element = document.getElementById(id);
            if (element) {
                // This captures the "Google Docs" feel
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });

                // Optional: Update the URL hash without jumping the page
                // history.pushState(null, null, `#${id}`);
            }
        },
        handleManualScroll() {
            const container = document.getElementById('main-col');
            if (!container) return;

            const containerRect = container.getBoundingClientRect();
            const newActiveSections = [];

            this.sectionIds.forEach(id => {
                const el = document.getElementById(id);
                if (el) {
                    const rect = el.getBoundingClientRect();

                    // Check if any part of the section is visible within the container's viewport
                    // We use a small offset (20px) to make the transition feel smoother
                    const isVisible = (rect.top < containerRect.bottom - 20) &&
                        (rect.bottom > containerRect.top + 20);

                    if (isVisible) {
                        newActiveSections.push(id);
                    }
                }
            });

            this.activeSections = newActiveSections;
        }
    },
    watch: {
        // watch the prop 'jobs' to load the config
        async jobs(newVal, oldVal) {
            if (newVal && Object.keys(newVal).length > 0) {
                this.loadConfig();
            }
        },
        loading(val) {
            if (!val) {
                // Wait for DOM to render after loading stops
                this.$nextTick(() => {
                    const container = document.getElementById('main-col');
                    if (container) container.addEventListener('scroll', this.handleManualScroll);
                });
            }
        },
        async saveTrigger() {
            await this.saveConfiguration(this.clusterConfig, this.oltpConfig, this.extractConfig);
        },
        async saveAndRunTrigger() {
            await this.saveConfigurationAndRun(this.clusterConfig, this.oltpConfig, this.extractConfig);
        },
    },
    emits: ['refreshJobsDetails', 'setEditButton', 'setEditMode', 'saving', 'promoting', 'getPromotionStatus', 'getSaveAndRunStatus', 'triggerPipelineStatusCheck'],
}
</script>

<style scoped>
.tab-content {
    min-height: 200px;
}

.tab-pane {
    display: none;
}

.tab-pane.show.active {
    display: block;
    animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(5px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.edit-tab-content {
    padding-top: 0.5rem;
}

.config-changes-tab-content {
    padding-top: 0.5rem;
}

.config-header {
    color: #2C7BE5;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
}

.custom-card-body {
    padding: 0 1.5rem 1.5rem 1.5rem;
}

/* Scrollbar styles */
.scroll-container {
    scroll-behavior: smooth;
}

.side-nav .nav-link {
    color: #6c757d;
    font-size: 0.95rem;
    transition: all 0.2s ease;
    border-left: 3px solid #d3d3d3;
    padding-left: 12px !important;
}

.side-nav .nav-link.active {
    color: #2C7BE5;
    font-weight: 500;
    background-color: #edf2f8;
    border-left: 3px solid #2C7BE5;
}

.side-nav .nav-link:hover {
    background-color: #f8f9fa;
}

.side-nav .first-nav-link {
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;
}

.side-nav .last-nav-link {
    border-bottom-left-radius: 3px;
    border-bottom-right-radius: 3px;
}

.sticky-top {
    position: sticky;
    background-color: #fff;
}

.tab-content {
    min-height: 200px;
}

.tab-pane.show.active {
    display: block;
    animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(5px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

html {
    scroll-behavior: smooth !important;
}

.layout-wrapper {
    max-height: calc(100vh - 100px);
    display: flex;
    align-items: flex-start; /* prevent children from stretching */
}

.main-col {
    flex: 1;
    max-height: calc(100vh - 100px);
    overflow-y: auto;
    scroll-behavior: smooth;
}
</style>

<style lang="scss" scoped>
@use '../../theme/scss/_user-variables.scss' as *;

body.dark-mode {
	a.nav-link {
		background-color: $dark-card-bg !important;
        border-color: $dark-text-secondary !important;
	}

	a.nav-link:hover {
		background-color: $dark-hover-bg !important;
	}

	a.nav-link.active {
		background-color: $dark-elevated-bg !important;
        border-color: $dark-accent !important;
	}
    .dark-mode-bg {
        background-color: $dark-card-bg !important;
    }
}
</style>